/**
 * Recording module - rrweb initialization and event handling
 */

import type { RRWebEvent } from './types';
import { sessionState, win } from './state';
import { IDLE_TIMEOUT, MAX_SESSION_LENGTH, PAUSE_THRESHOLD, IDLE_CHECK_INTERVAL } from './constants';
import { recordActivity, setupActivityListeners } from './activity';
import { endSession } from './session';
import { sendEventBatch } from './batch';
import { isNoiseConsoleError } from './noise-filter';
import { redactUrl } from './redact';
import type { TelemetryEvent } from './types';

/**
 * Filter event - check if event should be recorded
 * Extracted for testability
 */
export function shouldRecordEvent(event: RRWebEvent): boolean {
  // Don't record if paused
  if (sessionState.isRecordingPaused) {
    return false;
  }

  // Filter out sandbox-related and other non-actionable console errors
  if (event.type === 6 && event.data?.plugin === "rrweb/console@1") {
    const payload = event.data.payload;
    if (payload && payload.level === "error" && Array.isArray(payload.payload)) {
      const message = payload.payload.join(" ");
      // Skip sandbox-related errors
      if (message.includes("sandboxed")) {
        return false;
      }
      // Skip known noise: opaque cross-origin errors, ResizeObserver loops,
      // browser-extension errors, analytics network failures
      if (isNoiseConsoleError(message)) {
        return false;
      }
    }
  }

  return true;
}

/**
 * Scrub credentials out of the `href` rrweb writes into its own events.
 *
 * rrweb stamps the raw `window.location.href` into Meta events (type 4) at
 * record start and on every full snapshot. After an OAuth implicit-flow
 * redirect that href still holds the token fragment, and it flows downstream
 * into session_flags.url. rrweb builds these events itself, so they have to be
 * caught here rather than at our own capture sites.
 */
export function redactEventUrls(event: RRWebEvent): RRWebEvent {
  const href = (event.data as any)?.href;
  if (typeof href !== "string" || !href) return event;
  const safe = redactUrl(href);
  if (safe === href) return event;
  return { ...event, data: { ...(event.data as any), href: safe } };
}

/**
 * Handle incoming rrweb event
 */
export function handleEvent(event: RRWebEvent): void {
  if (!shouldRecordEvent(event)) {
    return;
  }

  sessionState.events.push(redactEventUrls(event));

  // Track event types for debugging
  sessionState.eventTypeCounts[event.type] = (sessionState.eventTypeCounts[event.type] || 0) + 1;

  // Record activity on user interactions
  if (event.type === 3) { // IncrementalSnapshot
    const data = event.data;
    // Mouse clicks, keyboard input, scroll
    if (data?.source === 2 || data?.source === 5 || data?.source === 3) {
      recordActivity();
    }
  }

  // Log click events
  if (event.type === 3 && event.data?.source === 2 && event.data?.type === 2) {
    recordActivity();
  }

  // Track errors from console recording plugin
  if (event.type === 6 && event.data?.plugin === "rrweb/console@1") {
    const payload = event.data.payload;
    if (payload && payload.level === "error") {
      sessionState.consoleErrorCount++;
    } else if (payload && payload.level === "warn") {
      sessionState.warningCount++;
    }
  }
}

/**
 * Record a structured telemetry event
 */
function recordTelemetryEvent(event: TelemetryEvent): void {
  sessionState.telemetryEvents.push(event);
}

/**
 * Collect Core Web Vitals using PerformanceObserver
 */
function startVitalsCollection(): void {
  if (typeof PerformanceObserver === "undefined") return;

  // LCP (Largest Contentful Paint)
  try {
    const lcpObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const lastEntry = entries[entries.length - 1];
      if (lastEntry) {
        recordTelemetryEvent({
          type: "vital",
          subtype: "lcp",
          timestamp: Date.now(),
          data: {
            value: (lastEntry as any).startTime || 0,
            element: (lastEntry as any).element?.tagName || undefined,
            size: (lastEntry as any).size || 0,
          },
        });
      }
    });
    lcpObserver.observe({ type: "largest-contentful-paint", buffered: true });
  } catch (e) {
    // Browser might not support LCP
  }

  // CLS (Cumulative Layout Shift)
  try {
    let clsValue = 0;
    const clsObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (!(entry as any).hadRecentInput) {
          clsValue += (entry as any).value;
        }
      }
      recordTelemetryEvent({
        type: "vital",
        subtype: "cls",
        timestamp: Date.now(),
        data: { value: clsValue },
      });
    });
    clsObserver.observe({ type: "layout-shift", buffered: true });
  } catch (e) {
    // Browser might not support CLS
  }

  // INP (Interaction to Next Paint) — best-effort via event timing
  try {
    const eventObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      let maxDuration = 0;
      for (const entry of entries) {
        const duration = (entry as any).duration || 0;
        if (duration > maxDuration) maxDuration = duration;
      }
      if (maxDuration > 0) {
        recordTelemetryEvent({
          type: "vital",
          subtype: "inp",
          timestamp: Date.now(),
          data: { value: maxDuration },
        });
      }
    });
    eventObserver.observe({ type: "event", buffered: true } as PerformanceObserverInit);
  } catch (e) {
    // Browser might not support event timing
  }

  // TTFB (Time to First Byte) — from navigation timing
  try {
    const navEntries = performance.getEntriesByType("navigation");
    if (navEntries.length > 0) {
      const nav = navEntries[0] as PerformanceNavigationTiming;
      recordTelemetryEvent({
        type: "vital",
        subtype: "ttfb",
        timestamp: Date.now(),
        data: {
          value: nav.responseStart,
          dns: nav.domainLookupEnd - nav.domainLookupStart,
          connect: nav.connectEnd - nav.connectStart,
          download: nav.responseEnd - nav.responseStart,
          domReady: nav.domContentLoadedEventEnd - nav.startTime,
          loadComplete: nav.loadEventEnd - nav.startTime,
        },
      });
    }
  } catch (e) {
    // Navigation timing not available
  }
}

/**
 * Initialize rrweb recording
 */
export function initializeRecording(): void {
  if (typeof win.rrweb === "undefined" || typeof win.rrweb.record !== "function") {
    return;
  }

  try {
    // Prepare plugins array
    const plugins: unknown[] = [];

    // Add console recording plugin if available
    if (
      typeof win.rrwebConsoleRecord !== "undefined" &&
      typeof win.rrwebConsoleRecord.getRecordConsolePlugin === "function"
    ) {
      plugins.push(win.rrwebConsoleRecord.getRecordConsolePlugin());
    }

    sessionState.stopRecording = win.rrweb!.record({
      emit: handleEvent,
      plugins: plugins,
      maskAllInputs: true,
      maskInputOptions: { password: true },
      checkoutEveryNth: 250,   // Full snapshot every 250 events (less frequent)
      checkoutEveryNms: 60000, // Full snapshot every 60 seconds (less frequent)
      recordCanvas: false,
      collectFonts: false,
      inlineStylesheet: true,
      sampling: {
        mousemove: 100,  // 10fps cursor — sufficient for UX analysis, much less CPU
        mouseInteraction: {
          MouseUp: false,
          MouseDown: false,
          Click: true,
          ContextMenu: false,
          DblClick: false,
          Focus: false,
          Blur: false,
          TouchStart: true,
          TouchEnd: false,
        },
        scroll: 250,    // Less frequent scroll sampling
        input: "last",
        media: 1000,
      },
      slimDOMOptions: {
        script: true,
        comment: true,
        headFavicon: true,
        headWhitespace: true,
        headMetaSocial: true,
        headMetaRobots: true,
        headMetaHttpEquiv: true,
        headMetaAuthorship: true,
        headMetaVerification: true,
      },
      recordCrossOriginIframes: false,
      blockClass: 'rr-block',
      ignoreClass: 'rr-ignore',
      maskTextClass: 'rr-mask',
      maskTextSelector: null,
      mutationBatchTime: 150,  // Batch mutations for 150ms — fewer events, less CPU
    });

    // Track uncaught errors
    window.addEventListener("error", function () {
      sessionState.errorCount++;
      recordActivity();
    });

    // Track unhandled promise rejections
    window.addEventListener("unhandledrejection", function () {
      sessionState.errorCount++;
      recordActivity();
    });

    // Track blocked paste events
    document.addEventListener("paste", function (e: ClipboardEvent) {
      const target = e.target as HTMLElement;
      // Check immediately — defaultPrevented is already set by the time the event bubbles
      if (e.defaultPrevented) {
        if (win.rrweb && typeof (win.rrweb as any).record?.addCustomEvent === "function") {
          (win.rrweb as any).record.addCustomEvent("paste_blocked", {
            elementId: (target as any).__sn?.id,
            tagName: target.tagName?.toLowerCase(),
            inputType: (target as HTMLInputElement).type,
            fieldName: (target as HTMLInputElement).name || (target as HTMLInputElement).placeholder || "",
            timestamp: Date.now()
          });
        }
      }
    }, true);

    // SPA navigation tracking — rrweb only records hard page loads (Meta
    // events). Route changes in single-page apps are invisible to journey
    // context, back-nav and quick_back detection without this.
    const emitNavigation = function (trigger: string) {
      try {
        if (win.rrweb && typeof (win.rrweb as any).record?.addCustomEvent === "function") {
          (win.rrweb as any).record.addCustomEvent("navigation", {
            href: redactUrl(window.location.href),
            trigger: trigger,
            timestamp: Date.now()
          });
        }
      } catch (e) { /* recording not ready */ }
    };
    (["pushState", "replaceState"] as const).forEach(function (method) {
      const original = history[method];
      history[method] = function (this: History, ...args: any[]) {
        const result = original.apply(this, args as any);
        emitNavigation(method);
        return result;
      } as any;
    });
    window.addEventListener("popstate", function () { emitNavigation("popstate"); });
    window.addEventListener("hashchange", function () { emitNavigation("hashchange"); });

    // Set up activity listeners for idle detection
    setupActivityListeners();

    // Start collecting Core Web Vitals and navigation timing
    startVitalsCollection();

    // Reset session start time and activity time
    const now = Date.now();
    sessionState.sessionStartTime = now;
    sessionState.lastActivityTime = now;

    // Set up periodic sending
    sessionState.sendIntervalId = window.setInterval(function () {
      if (!sessionState.sessionEnded) {
        sendEventBatch(false);
      }
    }, sessionState.sendInterval);

    // Set up idle detection and max session length check
    sessionState.idleCheckIntervalId = window.setInterval(function () {
      if (sessionState.sessionEnded) {
        return;
      }

      // Check for idle timeout
      const idleTime = Date.now() - sessionState.lastActivityTime;

      if (idleTime > IDLE_TIMEOUT) {
        endSession("idle_timeout");
        return;
      }

      // Check for max session length
      const sessionLength = Date.now() - sessionState.sessionStartTime;
      if (sessionLength > MAX_SESSION_LENGTH) {
        endSession("max_session_length");
      }
    }, IDLE_CHECK_INTERVAL);

    // Send session when page unloads
    // Use a diagnostic beacon to confirm the handler fires (readable in next session via localStorage)
    const finalizeSession = function (reason: string) {
      const timestamp = Date.now();
      const diagPayload = JSON.stringify({
        sessionId: sessionState.sessionId,
        reason: reason,
        timestamp: timestamp,
        eventsCount: sessionState.events.length,
        telemetryCount: sessionState.telemetryEvents.length
      });

      // Persist to localStorage so we can check if handler fired (tab closes before console logs are readable)
      try {
        localStorage.setItem('__tk_last_finalization_attempt', diagPayload);
      } catch (e) {}

      // Diagnostic beacon to a no-op endpoint — if this reaches the server, the handler fired
      const diagUrl = sessionState.backendUrl.replace('/ingest-v2', '/ingest-v2-diag');
      if (navigator.sendBeacon) {
        // text/plain avoids the CORS preflight that credentialed beacons fail
        navigator.sendBeacon(diagUrl, new Blob([diagPayload], { type: 'text/plain' }));
      }

      console.log(`[Session Replay] Finalizing session on ${reason}`);
      endSession(reason);
    };

    window.addEventListener("beforeunload", function () {
      finalizeSession("page_unload");
    });

    // pagehide fires on mobile and some desktop browsers where beforeunload doesn't
    window.addEventListener("pagehide", function () {
      finalizeSession("page_hide");
    });

    // unload as last-resort fallback
    window.addEventListener("unload", function () {
      finalizeSession("unload");
    });

    // Handle tab visibility changes
    document.addEventListener("visibilitychange", function () {
      if (document.hidden) {
        // Tab hidden - record time
        sessionState.tabHiddenTime = Date.now();

        if (sessionState.isMobile) {
          // Mobile: end session immediately - tab close doesn't fire beforeunload reliably
          endSession("mobile_tab_hidden");
        }
      } else {
        // Tab visible again
        if (sessionState.tabHiddenTime) {
          const hiddenDuration = Date.now() - sessionState.tabHiddenTime;

          if (hiddenDuration > PAUSE_THRESHOLD) {
            endSession("tab_hidden_timeout");
          } else {
            // Update activity time since user is back
            recordActivity();
          }

          sessionState.tabHiddenTime = null;
        }
      }
    });

    // Store stop function globally for debugging
    win.__sessionReplayStop = function () {
      endSession("manual_stop");
    };
  } catch (error) {
    // Silent failure
  }
}

/**
 * Load rrweb library and plugins
 */
export function loadRRWeb(): void {
  const rrwebScript = document.createElement("script");
  // Pinned: rrweb 2.x ships ESM-only dist files that never set window.rrweb,
  // so a classic <script> load silently breaks recording. @latest flipped to
  // 2.0 on 2026-06-01 and killed all recordings; 1.1.3 is the IIFE build.
  rrwebScript.src =
    "https://cdn.jsdelivr.net/npm/rrweb@1.1.3/dist/rrweb.min.js";

  rrwebScript.onerror = function () {
    // Silent failure
  };

  rrwebScript.onload = function () {
    // Load console recording plugin
    const consolePlugin = document.createElement("script");
    consolePlugin.src =
      "https://cdn.jsdelivr.net/npm/rrweb@1.1.3/dist/plugins/console-record.min.js";

    consolePlugin.onerror = function () {
      // Initialize without console plugin
      initializeRecording();
    };

    consolePlugin.onload = function () {
      // Initialize recording
      initializeRecording();
    };

    document.head.appendChild(consolePlugin);
  };

  document.head.appendChild(rrwebScript);
}
